import React, { useState, useEffect, useRef } from "react";
import { getFunctions, httpsCallable } from "firebase/functions";
import { db } from "../../../../config/firebase";
import { doc, getDoc } from "firebase/firestore";

interface ChatMessage {
  role: "user" | "coach";
  content: string;
}

interface Props {
  projectID: string;
  userID: string;
  stepNumber: number;
  stepTitle: string;
  projectTitle: string;
  onClose: () => void;
}

export const StepCoachModal: React.FC<Props> = ({
  projectID,
  userID,
  stepNumber,
  stepTitle,
  projectTitle,
  onClose,
}) => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [projectDescription, setProjectDescription] = useState("");
  const [error, setError] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Load project description on mount
  useEffect(() => {
    if (!userID || !projectID) return;
    const loadProjectDesc = async () => {
      try {
        const projectRef = doc(db, "users", userID, "projects", projectID);
        const snap = await getDoc(projectRef);
        if (snap.exists()) {
          setProjectDescription(snap.data().projectDescription ?? "");
        }
      } catch {
        // non-critical — coach still works without description
      }
    };
    loadProjectDesc();
  }, [userID, projectID]);

  // Set welcome message after mount
  useEffect(() => {
    setMessages([{
      role: "coach",
      content: `Hi! I'm your AI step coach for **Step ${stepNumber}: ${stepTitle}**.\n\nAsk me anything about this step — what it means, how to complete it, or how it applies to ${projectTitle || "your business"}. I'll use your project context to give you specific guidance.`,
    }]);
  }, [stepNumber, stepTitle, projectTitle]);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleSend = async () => {
    const question = input.trim();
    if (!question || isLoading) return;

    const userMessage: ChatMessage = { role: "user", content: question };
    const updatedMessages = [...messages, userMessage];
    setMessages(updatedMessages);
    setInput("");
    setIsLoading(true);
    setError(null);

    try {
      const functions = getFunctions();
      const askAI = httpsCallable(functions, "askFieldAI");

      const conversationHistory = updatedMessages
        .slice(0, -1) // exclude the just-added user message; it's sent as userQuestion
        .map((m) => `${m.role === "user" ? "Founder" : "Coach"}: ${m.content}`)
        .join("\n");

      const result = await askAI({
        projectId: projectID,
        stepNumber,
        fieldName: "step_coach",
        fieldLabel: stepTitle,
        currentValue: question,
        userQuestion: question,
        mode: "coach",
        projectDescription: `${projectTitle ? "Project: " + projectTitle + ". " : ""}${projectDescription}`,
        conversationHistory,
      });

      const data = result.data as { suggestion: string };
      setMessages((prev) => [...prev, { role: "coach", content: data.suggestion }]);
    } catch {
      setError("Failed to get a response. Please try again.");
      setMessages((prev) => prev.slice(0, -1)); // remove the user message on error
      setInput(question); // restore their input
    } finally {
      setIsLoading(false);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const renderMessageContent = (content: string) => {
    // Very lightweight bold/newline rendering without a markdown library
    return content.split("\n").map((line, i) => {
      const parts = line.split(/\*\*(.*?)\*\*/g);
      return (
        <span key={i}>
          {parts.map((part, j) =>
            j % 2 === 1 ? <strong key={j}>{part}</strong> : part
          )}
          {i < content.split("\n").length - 1 && <br />}
        </span>
      );
    });
  };

  return (
    <div
      className="modal d-block"
      tabIndex={-1}
      role="dialog"
      style={{ backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1060 }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        className="modal-dialog modal-dialog-centered modal-dialog-scrollable"
        style={{ maxWidth: "min(600px, calc(100vw - 24px))", width: "100%" }}
        role="document"
      >
        <div className="modal-content" style={{ borderRadius: "12px", overflow: "hidden" }}>
          {/* Header */}
          <div
            className="modal-header"
            style={{
              background: "linear-gradient(135deg, #3B82F6 0%, #6366F1 100%)",
              border: "none",
              padding: "16px 20px",
            }}
          >
            <div className="d-flex align-items-center gap-3">
              <div
                style={{
                  width: "36px",
                  height: "36px",
                  borderRadius: "50%",
                  background: "rgba(255,255,255,0.2)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <i className="bi bi-robot text-white" style={{ fontSize: "16px" }} />
              </div>
              <div>
                <h5 className="modal-title text-white fw-bold mb-0" style={{ fontSize: "15px" }}>
                  Step Coach
                </h5>
                <span style={{ fontSize: "12px", color: "rgba(255,255,255,0.75)" }}>
                  Step {stepNumber}: {stepTitle}
                </span>
              </div>
            </div>
            <button
              type="button"
              className="btn btn-icon btn-sm"
              onClick={onClose}
              style={{ color: "rgba(255,255,255,0.8)" }}
              aria-label="Close"
            >
              <i className="bi bi-x-lg" style={{ fontSize: "18px" }} />
            </button>
          </div>

          {/* Messages */}
          <div
            className="modal-body"
            style={{
              padding: "16px",
              height: "clamp(200px, 45vh, 380px)",
              overflowY: "auto",
              background: "#F8FAFC",
            }}
          >
            {messages.map((msg, idx) => (
              <div
                key={idx}
                className={`d-flex ${msg.role === "user" ? "justify-content-end" : "justify-content-start"} mb-3`}
              >
                {msg.role === "coach" && (
                  <div
                    style={{
                      width: "28px",
                      height: "28px",
                      borderRadius: "50%",
                      background: "#E0E7FF",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                      marginRight: "8px",
                      marginTop: "2px",
                    }}
                  >
                    <i className="bi bi-robot" style={{ fontSize: "12px", color: "#4F46E5" }} />
                  </div>
                )}
                <div
                  style={{
                    maxWidth: "90%",
                    padding: "10px 14px",
                    borderRadius: msg.role === "user" ? "16px 16px 4px 16px" : "16px 16px 16px 4px",
                    background: msg.role === "user" ? "#3B82F6" : "#FFFFFF",
                    color: msg.role === "user" ? "#FFFFFF" : "#1E293B",
                    fontSize: "13px",
                    lineHeight: "1.5",
                    boxShadow: "0 1px 2px rgba(0,0,0,0.06)",
                    border: msg.role === "coach" ? "1px solid #E2E8F0" : "none",
                  }}
                >
                  {renderMessageContent(msg.content)}
                </div>
              </div>
            ))}

            {isLoading && (
              <div className="d-flex justify-content-start mb-3">
                <div
                  style={{
                    width: "28px",
                    height: "28px",
                    borderRadius: "50%",
                    background: "#E0E7FF",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                    marginRight: "8px",
                    marginTop: "2px",
                  }}
                >
                  <i className="bi bi-robot" style={{ fontSize: "12px", color: "#4F46E5" }} />
                </div>
                <div
                  style={{
                    padding: "10px 14px",
                    borderRadius: "16px 16px 16px 4px",
                    background: "#FFFFFF",
                    border: "1px solid #E2E8F0",
                    fontSize: "13px",
                    color: "#94A3B8",
                  }}
                >
                  <span className="spinner-border spinner-border-sm me-2" style={{ width: "12px", height: "12px" }} />
                  Thinking...
                </div>
              </div>
            )}

            {error && (
              <div className="alert alert-danger py-2 px-3 fs-7 mb-2">
                <i className="bi bi-exclamation-triangle me-2" />
                {error}
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div
            className="modal-footer"
            style={{ padding: "12px 16px", border: "1px solid #E2E8F0", background: "#FFFFFF" }}
          >
            <div className="d-flex align-items-end gap-2 w-100">
              <textarea
                ref={inputRef}
                className="form-control"
                placeholder="Ask anything about this step… (Enter to send, Shift+Enter for newline)"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                rows={2}
                style={{
                  resize: "none",
                  fontSize: "13px",
                  borderRadius: "8px",
                  border: "1px solid #CBD5E1",
                }}
                disabled={isLoading}
              />
              <button
                className="btn btn-primary"
                onClick={handleSend}
                disabled={!input.trim() || isLoading}
                style={{
                  borderRadius: "8px",
                  padding: "10px 16px",
                  flexShrink: 0,
                  height: "62px",
                }}
              >
                {isLoading ? (
                  <span className="spinner-border spinner-border-sm" style={{ width: "14px", height: "14px" }} />
                ) : (
                  <i className="bi bi-send-fill" style={{ fontSize: "14px" }} />
                )}
              </button>
            </div>
            <p className="text-muted fs-8 mb-0 w-100 mt-1">
              Session only — conversation clears when you close this modal.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
